import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './SelectedShape.styles';

import {
  BACKSPLASH_INFO_IMAGE,
  EDGE_LENGTH_INPUT_LIMIT,
  EDGE_OPTIONS_INFO_IMAGE,
} from '~/core/constants';
import { UserDesignOperation } from '~/core/enums';
import { updateShapeByEdge } from '~/core/helpers';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { ArrowInfo, DrawingShape, SideItemInfoModel } from '~/core/models';
import { enforceEdgeConstraints } from '~/core/utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { useDrawingShapeContext } from '~contexts';
import InputBoxCustom from '~molecules/InputBoxCustom';
import SideItemOption from '~molecules/SideItemOption';
import { useArrowSideItemInfoModel } from '~molecules/SideItemOption/hooks/useArrowSideItemInfoModel';

type ModalInfo = 'countertop' | 'backsplash';

const REQUIRED_CLICKS_TO_EDIT = 2;

const SelectedShape = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    getters: {
      activeDrawingShape: _activeDrawingShape,
      actualDrawingShapes,
      showNotification,
    },
    handlers: {
      updateDrawingShapes,
      updateActiveDrawingShapeId,
      updateShowNotification,
    },
  } = useDrawingShapeContext();

  const { getArrowSideItems } = useArrowSideItemInfoModel();
  const { registerUserAction } = useRegisterUserAction();

  const [currentModal, setCurrentModal] = useState<ModalInfo | undefined>();
  const [editingLabelId, setEditingLabelId] = useState<
    DrawingShape['id'] | undefined
  >();
  const editingLabel = useRef('');
  // Parent component `SidePanelShapeTab` makes sure `activeDrawingShape` is not undefined
  const activeDrawingShape = _activeDrawingShape as DrawingShape;
  const arrowsWithSideItems = (activeDrawingShape.arrowsInfo ?? [])
    .filter(
      (arrow) => !arrow.isInterior && !arrow.isDiagonalCorner && !!arrow.label
    )
    .sort(
      // Sort by length and then alphabetically
      ({ label: label1 = '' }, { label: label2 = '' }) =>
        Math.sign(label1.length - label2.length) || label1.localeCompare(label2)
    )
    .map((arrow) => {
      const sideItems = getArrowSideItems(arrow) ?? [];

      return [arrow, sideItems] satisfies [ArrowInfo, SideItemInfoModel[]];
    });

  const openModal = (currentModal: ModalInfo) => (isOpen: boolean) =>
    isOpen ? setCurrentModal(currentModal) : undefined;

  const activateShapeAndEditLabel =
    (shape: DrawingShape) => (e: React.MouseEvent<HTMLButtonElement>) => {
      // Pass forceUpdate=true to ensure dimension letters are shown even when
      // selecting the same shape from Sides Panel (matching behavior when clicking directly on shapes)
      updateActiveDrawingShapeId(shape.id, { forceUpdate: true });

      if (e.detail >= REQUIRED_CLICKS_TO_EDIT && shape.label) {
        editingLabel.current = shape.label;
        setEditingLabelId(shape.id);
      }
    };

  const changeShapeLabel = useCallback(
    (currentLabel = '') =>
      () => {
        const newLabel = editingLabel.current.trim();

        if (newLabel && newLabel !== currentLabel) {
          updateDrawingShapes((dShapes) =>
            dShapes.map((shape) =>
              shape.id === editingLabelId
                ? { ...shape, label: newLabel }
                : shape
            )
          );
          editingLabel.current = '';
          dispatch(updateNeedRedrawCanvas(true));
          registerUserAction(UserDesignOperation.MODIFY_SHAPE_NAME);
        } else {
          editingLabel.current = currentLabel;
        }

        setEditingLabelId(undefined);
      },
    [dispatch, editingLabelId, registerUserAction, updateDrawingShapes]
  );

  const updateArrowSize =
    (shape: DrawingShape, arrow: ArrowInfo) => (value: number) => {
      const size = value
        ? enforceEdgeConstraints({ arrow, value, shape })
        : arrow.size;

      updateShapeByEdge({ ...arrow, size }, shape, false);
      updateDrawingShapes((dShapes) => [...dShapes]);
      dispatch(updateNeedRedrawCanvas(true));
      updateShowNotification({
        ...showNotification,
        [activeDrawingShape.id]: false,
      });
      registerUserAction(UserDesignOperation.MODIFY_SHAPE_SIZE);
    };

  return (
    <St.Container>
      <St.ShapeLabels>
        {actualDrawingShapes.map((shape) =>
          shape.id === editingLabelId ? (
            <St.Input
              key={shape.id + shape.label}
              autoFocus
              data-is-active={activeDrawingShape.id === shape.id}
              placeholder="loading shape..."
              defaultValue={editingLabel.current}
              maxLength={20}
              onChange={(e) => (editingLabel.current = e.target.value)}
              onBlur={changeShapeLabel(shape.label)}
              onPressEnter={(e) => e.currentTarget.blur()}
            />
          ) : (
            <St.Button
              key={shape.id + shape.label}
              data-is-active={activeDrawingShape.id === shape.id}
              onClick={activateShapeAndEditLabel(shape)}
            >
              {shape.label}
            </St.Button>
          )
        )}
      </St.ShapeLabels>

      <St.LabelsDivider />

      {activeDrawingShape.id in showNotification && (
        <St.AdjustDimensionsMsg>
          {t('SHAPE_TAB.NOTIFICATION')}
        </St.AdjustDimensionsMsg>
      )}

      <St.ShapeOptions>
        {arrowsWithSideItems.map(([arrowInfo, sideItems]) => (
          <St.ShapeSideContainer
            key={`${activeDrawingShape.id}_${arrowInfo.label}`}
          >
            <St.SideTitle>{arrowInfo.label}</St.SideTitle>
            <InputBoxCustom
              size={arrowInfo.size ?? EDGE_LENGTH_INPUT_LIMIT.MIN}
              arrowInfo={arrowInfo}
              onUpdatedValue={updateArrowSize(activeDrawingShape, arrowInfo)}
            />
            <St.SideItemOptions>
              {sideItems.map((sideItem) => (
                <SideItemOption
                  key={`${arrowInfo.label}_${sideItem.label}`}
                  id={`${arrowInfo.label}_${sideItem.label}`}
                  sideItem={sideItem}
                  onInfoModalOpen={openModal}
                />
              ))}
            </St.SideItemOptions>
          </St.ShapeSideContainer>
        ))}
      </St.ShapeOptions>

      <St.Modal
        type="info"
        isVisible={!!currentModal}
        onClose={() => setCurrentModal(undefined)}
        title={t(modalInfo[currentModal as ModalInfo]?.title) ?? ''}
        content={
          <St.ModalImage src={modalInfo[currentModal as ModalInfo]?.imgSrc} />
        }
        footer={null}
      />
    </St.Container>
  );
};

export default SelectedShape;

const modalInfo: Record<ModalInfo, { title: string; imgSrc: string }> = {
  countertop: {
    title: 'SCROLLABLE_AREA.COUNTERTOP.MODAL_DESCRIPTION',
    imgSrc: EDGE_OPTIONS_INFO_IMAGE,
  },
  backsplash: {
    title: 'SHAPE_TAB.BACKSPLASH_INFO',
    imgSrc: BACKSPLASH_INFO_IMAGE,
  },
};
